import {
  AloftArea,
  AloftPrice,
  SiteRating,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schPairedCostRouteItem,
  schSiteRating,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';

export const inconsistentCompGlaAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  propertyDataEntities,
}) => {
  const subject = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;

  const subjectGla = orUndefined(schAloftArea.parse)(subject?.improvements?.gla);
  const subjectInfluenceRating = orUndefined(schSiteRating.parse)(subject?.site?.influenceRating);
  const subjectViewRating = orUndefined(schSiteRating.parse)(subject?.site?.viewRating);

  if (!subjectGla || !subjectInfluenceRating || !subjectViewRating) return undefined;

  const faultyProperty = compSequence
    .map((id, index) => {
      const propertyData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
        .editableData;

      const gla = propertyData?.improvements?.gla;
      const influenceRating = propertyData?.site?.influenceRating;
      const viewRating = propertyData?.site?.viewRating;

      const glaAdjustment = compAdjustments[id]?.mainDwelling.gla.adjustment;

      return {
        gla: orUndefined(schAloftArea.parse)(gla),
        glaAdjustment: orUndefined(schAloftPrice.parse)(glaAdjustment),
        id,
        index,
        influenceRating: orUndefined(schSiteRating.parse)(influenceRating),
        viewRating: orUndefined(schSiteRating.parse)(viewRating),
      };
    })
    .filter(
      (
        c,
      ): c is {
        gla: AloftArea;
        glaAdjustment: AloftPrice;
        id: string;
        index: number;
        influenceRating: SiteRating;
        viewRating: SiteRating;
      } => !!c.gla && !!c.glaAdjustment && !!c.influenceRating && !!c.viewRating,
    )
    .find(({ gla, glaAdjustment, influenceRating, viewRating }) => {
      if (influenceRating !== subjectInfluenceRating) return false;
      if (viewRating !== subjectViewRating) return false;

      if (glaAdjustment.lt(AloftPrice.zero) && gla.lt(subjectGla)) return true;
      if (glaAdjustment.gt(AloftPrice.zero) && gla.gt(subjectGla)) return true;

      return false;
    });

  if (!faultyProperty) return undefined;

  const { id: propertyID, gla, glaAdjustment } = faultyProperty;
  const propertyName =
    propertyID === 'subject' ? 'Subject' : `Comparable ${faultyProperty.index + 1}`;

  let glaRelationship = 'an equal';
  if (gla.gt(subjectGla)) {
    glaRelationship = 'a larger';
  } else if (gla.lt(subjectGla)) {
    glaRelationship = 'a smaller';
  }

  return {
    type: schAlertType.enum.inconsistentCompGlaAdjustment,
    status: 'active',
    message: `${propertyName} is adjusted ${glaAdjustment.gt(AloftPrice.zero) ? 'up' : 'down'}, despite having ${glaRelationship} GLA than the Subject (${propertyName}: ${gla.toString()} | Subject: ${subjectGla.toString()}).`,
    propertyID,
    fixingInfo: {
      tips: 'Tips: The GLA adjustment appears to be going in the wrong direction. The Subject and Comparable have the same view and site influences, but the adjustment does not align with the difference in GLA.',
      pairedCostRouteItem: schPairedCostRouteItem.enum.analysis,
    },
  };
};
